import TagService from '@bo/services/tagService';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import FormGroup from '@sharedComponents/FormGroup';
import InputSelect from '@sharedComponents/InputSelect';
import InputText from '@sharedComponents/InputText';
import InputTextArea from '@sharedComponents/InputTextArea';
import Loader from '@sharedComponents/Loader';
import TabBar from '@sharedComponents/TabBar';
import { showToast } from '@sharedComponents/Toast';
import Typography from '@sharedComponents/Typography';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const tagService = new TagService();

const TYPE_OPTIONS = [
    { value: 'typology', label: 'Typology' },
    { value: 'domain', label: 'Domain' },
    { value: 'family', label: 'Family' },
];

const TagForm = ({ defaultValues, tagId, defaultType, onSuccess, onBack, languagesOptions = [], optionsLoading = false } = {}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [activeLang, setActiveLang] = useState(null);

    const {
        register,
        watch,
        setValue,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: defaultValues ?? {
            type: defaultType ?? 'typology',
            valeur: {},
            motsCles: '',
        },
    });

    useEffect(() => {
        if (languagesOptions.length > 0 && activeLang === null) {
            setActiveLang(languagesOptions[0].label);
        }
    }, [languagesOptions, activeLang]);

    const currentType = watch('type');

    const onSubmit = async (data) => {
        console.log('Form data to submit:', data);
        const payload = {
            type: data.type,
            valeur: JSON.stringify(data.valeur),
            motsCles: data.motsCles || null,
        };
        setIsLoading(true);
        try {
            const result = tagId ? await tagService.updateTag(tagId, payload) : await tagService.createTag(payload);
            showToast({
                type: 'success',
                title: tagId ? t('Tag updated') : t('Tag created'),
                text: t('The tag "{{name}}" has been saved.', {
                    name: data.valeur[activeLang] ?? Object.values(data.valeur)[0] ?? '',
                }),
            });
            onSuccess?.(result);
        } catch {
            showToast({ type: 'error', title: t('Error'), text: t('An error occurred while saving.') });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            await tagService.deleteTag(tagId);
            showToast({ type: 'success', title: t('Tag deleted'), text: t('The tag has been deleted.') });
            onSuccess?.();
        } catch (error) {
            if (error.status === 409) {
                showToast({
                    type: 'error',
                    title: t('Deletion impossible'),
                    text: error.response?.error ?? t('This tag is used by one or more collections and cannot be deleted.'),
                });
            } else {
                showToast({ type: 'error', title: t('Error'), text: t('An error occurred while deleting.') });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FlexBox flexDirection="column" gap="l">
            {onBack && <Button action={onBack} label={t('← Back')} />}

            <Typography fontWeight="bold" fontSize="xl">
                {tagId ? t('Edit tag') : t('New tag')}
            </Typography>

            {optionsLoading ? (
                <FlexBox justifyContent="center">
                    <Loader />
                </FlexBox>
            ) : (
                <form>
                    <FlexBox gap="m" flexDirection="column">
                        <FormGroup label={t('Type')} errorMsg={errors.type?.message}>
                            <InputSelect
                                value={TYPE_OPTIONS.find((o) => o.value === currentType) ?? null}
                                options={TYPE_OPTIONS}
                                onChange={(opt) => setValue('type', opt?.value ?? 'typology')}
                                isDisabled={!!defaultType}
                            />
                        </FormGroup>

                        <FlexBox flexDirection="column" gap="s">
                            <TabBar
                                tabs={languagesOptions.map((lang) => ({
                                    label: lang.label,
                                    selected: activeLang === lang.label,
                                }))}
                                onClick={(index) => setActiveLang(languagesOptions[index].label)}
                            />
                            {languagesOptions.map((lang) => (
                                <div key={lang.label} style={{ display: activeLang === lang.label ? 'block' : 'none' }}>
                                    <FormGroup label={t('Label')} errorMsg={errors.valeur?.[lang.label]?.message}>
                                        <InputText
                                            {...register(`valeur.${lang.label}`, { required: t('Label is required') })}
                                            isError={!!errors.valeur?.[lang.label]}
                                        />
                                    </FormGroup>
                                </div>
                            ))}
                        </FlexBox>

                        <FormGroup label={t('Keywords')}>
                            <InputTextArea {...register('motsCles')} />
                        </FormGroup>

                        <FlexBox justifyContent="center">
                            <Button
                                style={{ width: '300px' }}
                                action={() => onSubmit(getValues())}
                                disabled={isLoading}
                                label={isLoading ? <Loader /> : tagId ? t('Update tag') : t('Save tag')}
                            />
                        </FlexBox>

                        {tagId && !showDeleteConfirm && (
                            <Button
                                style={{ backgroundColor: 'red' }}
                                action={() => setShowDeleteConfirm(true)}
                                disabled={isLoading}
                                label={t('Delete tag')}
                            />
                        )}

                        {tagId && showDeleteConfirm && (
                            <FlexBox flexDirection="column" gap="s">
                                <Typography>{t('Are you sure you want to delete this tag?')}</Typography>
                                <FlexBox gap="s">
                                    <Button
                                        style={{ backgroundColor: 'red' }}
                                        action={handleDelete}
                                        disabled={isLoading}
                                        label={isLoading ? <Loader /> : t('Confirm deletion')}
                                    />
                                    <Button action={() => setShowDeleteConfirm(false)} disabled={isLoading} label={t('Cancel')} />
                                </FlexBox>
                            </FlexBox>
                        )}
                    </FlexBox>
                </form>
            )}
        </FlexBox>
    );
};

export default TagForm;
